import React, { useEffect, useState, useContext } from "react";
import { toast } from "react-toastify";
import { Loader2, Package, File as FileIcon } from "lucide-react";

import { Button } from "../ui/button";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogFooter,
} from "../ui/dialog";
import PageTabs from "../PageTabs";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { getPublicFileUrl } from "../../config";
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";

const MediaLibraryPicker = ({ open, onClose, ticketId, quotedMsg }) => {
	// Garantia máxima da mensagem de resposta: lê do contexto DIRETO no momento
	// do clique, independente da prop `quotedMsg` (que pode ficar stale se o
	// picker abriu antes do usuário clicar em Responder).
	const { replyingMessage: replyingMessageDoContexto, setReplyingMessage } = useContext(ReplyMessageContext);

	const montarQuotedMsg = () => {
		const m = quotedMsg?.id ? quotedMsg : replyingMessageDoContexto;
		if (!m?.id) return undefined;
		return {
			id: m.id,
			fromMe: Boolean(m.fromMe),
			body: m.body || "",
		};
	};

	const [itens, setItens] = useState([]);
	const [pacotes, setPacotes] = useState([]);
	const [aba, setAba] = useState("arquivos");
	const [carregando, setCarregando] = useState(false);
	const [enviando, setEnviando] = useState(null);

	useEffect(() => {
		if (!open) return;

		(async () => {
			setCarregando(true);
			try {
				const [resItens, resPacotes] = await Promise.all([
					api.get("/media-library"),
					api.get("/media-packs"),
				]);
				setItens(resItens.data?.items || (Array.isArray(resItens.data) ? resItens.data : []));
				setPacotes(Array.isArray(resPacotes.data) ? resPacotes.data : []);
			} catch (err) {
				toastError(err);
			}
			setCarregando(false);
		})();
	}, [open]);

	const enviar = async (item) => {
		setEnviando(item.id);
		try {
			const quotedMontado = montarQuotedMsg();
			const payload = { ticketId };
			if (quotedMontado) payload.quotedMsg = quotedMontado;
			// eslint-disable-next-line no-console
			console.log(
				"[MediaLibraryPicker:enviar] ticketId=",
				ticketId,
				"itemId=",
				item.id,
				"quotedMontado=",
				JSON.stringify(quotedMontado || null),
				"payload=",
				JSON.stringify(payload),
			);
			await api.post(`/media-library/${item.id}/send`, payload);
			toast.success("Arquivo enviado.");
			setReplyingMessage(null);
			onClose();
		} catch (err) {
			toastError(err);
		}
		setEnviando(null);
	};

	const enviarPacote = async (pacote) => {
		setEnviando(`pack-${pacote.id}`);
		try {
			const quotedMontado = montarQuotedMsg();
			const payload = { ticketId };
			if (quotedMontado) payload.quotedMsg = quotedMontado;
			// eslint-disable-next-line no-console
			console.log(
				"[MediaLibraryPicker:enviarPacote] ticketId=",
				ticketId,
				"packId=",
				pacote.id,
				"quotedMontado=",
				JSON.stringify(quotedMontado || null),
				"payload=",
				JSON.stringify(payload),
			);
			const { data } = await api.post(`/media-packs/${pacote.id}/send`, payload);

			// O envio segue mesmo se um arquivo falhar: quem manda precisa saber
			// que faltou um, senão acha que o cliente recebeu tudo.
			if (data.falhas > 0) {
				toast.warning(
					`${data.enviados} arquivo(s) enviado(s), ${data.falhas} falharam.`
				);
			} else {
				toast.success(`${data.enviados} arquivo(s) enviado(s).`);
			}
			setReplyingMessage(null);
			onClose();
		} catch (err) {
			toastError(err);
		}
		setEnviando(null);
	};

	const urlDo = (item) => getPublicFileUrl(item.mediaUrl);

	const EXT_IMG = new Set(["png", "jpg", "jpeg", "jfif", "webp", "gif", "bmp", "svg", "avif"]);
	const EXT_VID = new Set(["mp4", "webm", "mov", "mkv", "avi", "m4v"]);
	const EXT_AUD = new Set(["mp3", "wav", "ogg", "aac", "flac", "m4a", "opus"]);

	const pegarTipo = (item) => {
		const mt = String(item.mediaType || "").toLowerCase();
		if (mt === "sticker") return "image";
		if (mt.startsWith("image")) return "image";
		if (mt.startsWith("video")) return "video";
		if (mt.startsWith("audio")) return "audio";
		try {
			const ext = String(item.mediaUrl || "")
				.split("?")[0].split("#")[0].toLowerCase()
				.split(".").pop();
			if (ext) {
				if (EXT_IMG.has(ext)) return "image";
				if (EXT_VID.has(ext)) return "video";
				if (EXT_AUD.has(ext)) return "audio";
			}
		} catch {
			/* ignore */
		}
		return "doc";
	};

	return (
		<Dialog open={open} onOpenChange={(v) => !v && onClose()}>
			<DialogContent className="sm:max-w-lg">
				<DialogHeader>
					<DialogTitle>Enviar da biblioteca</DialogTitle>
				</DialogHeader>

				{/* A aba de pacotes só aparece quando existe algum: sem isso seria
				    uma aba vazia para toda empresa que não usa o recurso. */}
				{pacotes.length > 0 && (
					<PageTabs
						abas={[
							{ value: "arquivos", rotulo: "Arquivos", icone: FileIcon },
							{
								value: "pacotes",
								rotulo: "Pacotes",
								icone: Package,
								contador: pacotes.length,
							},
						]}
						atual={aba}
						onChange={setAba}
					/>
				)}

				{carregando ? (
					<div className="flex justify-center py-10">
						<Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
					</div>
				) : aba === "pacotes" ? (
					<div className="max-h-[55vh] space-y-2 overflow-y-auto">
						{pacotes.map((pacote) => (
							<button
								key={pacote.id}
								type="button"
								disabled={!!enviando}
								onClick={() => enviarPacote(pacote)}
								className="flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors hover:bg-accent disabled:opacity-60"
							>
								{enviando === `pack-${pacote.id}` ? (
									<Loader2 className="h-5 w-5 shrink-0 animate-spin" />
								) : (
									<Package className="h-5 w-5 shrink-0 text-muted-foreground" />
								)}
								<div className="min-w-0 flex-1">
									<p className="truncate text-sm font-medium">{pacote.name}</p>
									<p className="truncate text-xs text-muted-foreground">
										{pacote.items?.length || 0} arquivo(s)
										{pacote.description ? ` · ${pacote.description}` : ""}
									</p>
								</div>
							</button>
						))}
					</div>
				) : itens.length === 0 ? (
					<p className="py-10 text-center text-sm text-muted-foreground">
						Nenhum arquivo na biblioteca ainda.
					</p>
				) : (
					<div className="grid max-h-[55vh] grid-cols-3 gap-3 overflow-y-auto">
						{itens.map((item) => (
							<button
								key={item.id}
								type="button"
								disabled={!!enviando}
								onClick={() => enviar(item)}
								className="group space-y-1 rounded-lg border p-2 text-left transition-colors hover:bg-accent disabled:opacity-60"
							>
								<div className="flex h-24 items-center justify-center overflow-hidden rounded bg-muted/50">
								{enviando === item.id ? (
									<Loader2 className="h-5 w-5 animate-spin" />
								) : (() => {
									const t = pegarTipo(item);
									const u = urlDo(item);
									if (t === "image") {
										return (
											<img
												src={u}
												alt={item.name}
												loading="lazy"
												draggable={false}
												className="max-h-24 max-w-full bg-white object-contain"
											/>
										);
									}
									if (t === "video") {
										return (
											<video
												src={u}
												muted
												preload="metadata"
												playsInline
												className="max-h-24 max-w-full bg-black object-contain"
											/>
										);
									}
									if (t === "audio") {
										return (
											<audio
												src={u}
												preload="metadata"
												controls
												controlsList="nodownload noremoteplayback"
												className="h-8 max-w-[160px]"
											/>
										);
									}
									return <FileIcon className="h-7 w-7 text-muted-foreground" />;
								})()}
							</div>
								<p className="truncate text-xs font-medium">{item.name}</p>
							</button>
						))}
					</div>
				)}

				<DialogFooter>
					<Button variant="outline" onClick={onClose}>
						Fechar
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default MediaLibraryPicker;
